import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  FileZipOutlined,
  LoadingOutlined,
  PlusOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { FilterBar, FilterRow } from "@components/FilterBar";
import ObjectSelect from "@components/ObjectSelect";
import { useAdminContracts } from "@features/contracts/api";
import { useObjects } from "@features/devices/api";
import { PageLayoutWithHelp } from "@features/page-help";
import {
  downloadRegulatoryReport,
  downloadRegulatoryReportArchive,
  type RegulatoryReportResponse,
  useGenerateReport,
  useRegulatoryReports,
} from "@features/regulatory-reports/api";
import { useGlobalObject } from "@shared/hooks/useGlobalObject";
import { isAdminUser } from "@shared/rbac";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { useStoredUser } from "@shared/useStoredUser";
import {
  App,
  Button,
  Collapse,
  DatePicker,
  Modal,
  Progress,
  Radio,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";

const { Text } = Typography;

const statusMap: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  pending: { color: "default", icon: <ClockCircleOutlined />, label: "Ожидание" },
  generating: { color: "processing", icon: <LoadingOutlined />, label: "Генерация" },
  success: { color: "success", icon: <CheckCircleOutlined />, label: "Готов" },
  error: { color: "error", icon: <CloseCircleOutlined />, label: "Ошибка" },
};

const roundModeLabels: Record<number, string> = {
  0: "С накоплением (рекомендуется)",
  1: "Арифметическое",
  2: "Без округления",
};

const handleDownload = async (
  id: string,
  filename: string,
  messageApi: ReturnType<typeof App.useApp>["message"],
) => {
  try {
    const blob = await downloadRegulatoryReport(id);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch {
    messageApi.error("Не удалось скачать файл");
  }
};

const handleArchiveDownload = async (
  type: string,
  regionId: number,
  objectId: number,
  month: string,
  messageApi: ReturnType<typeof App.useApp>["message"],
) => {
  try {
    const blob = await downloadRegulatoryReportArchive({
      type,
      region_id: regionId,
      object_id: objectId,
      month,
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `80020_${month}.zip`;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch {
    messageApi.error("Не удалось скачать архив");
  }
};

// ── Completeness status types ─────────────────────────────────────────────

interface MonthlyStatusItem {
  contract_id: string;
  contract_number: string;
  company_name: string;
  region_id: number;
  object_id: number;
  days_in_month: number;
  reports_success: number;
  reports_error: number;
  reports_missing: number;
  meters_total: number;
  meters_without_code: number;
  is_complete: boolean;
}

// ── Main Component ────────────────────────────────────────────────────────

const RegulatoryReportsPage = () => {
  const { message: messageApi, modal } = App.useApp();
  const user = useStoredUser();
  const isAdmin = isAdminUser(user);

  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];
  const { selectedObject, setSelectedObject } = useGlobalObject(objects);

  const [month, setMonth] = useState<dayjs.Dayjs | null>(dayjs().startOf("month"));
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  // Generate modal state.
  const [generateOpen, setGenerateOpen] = useState(false);
  const [genPeriodType, setGenPeriodType] = useState<"day" | "month">("day");
  const [genDay, setGenDay] = useState<dayjs.Dayjs | null>(dayjs());
  const [genMonth, setGenMonth] = useState<dayjs.Dayjs | null>(dayjs().subtract(1, "month"));
  const [genRound, setGenRound] = useState(0);
  const [genContractId, setGenContractId] = useState<string | undefined>(undefined);

  const regionID = selectedObject?.regionId;
  const objectID = selectedObject?.objectId;
  const monthStr = month?.format("YYYY-MM");

  // Contracts for the selected object (for generation modal).
  const contractsQuery = useAdminContracts(undefined, isAdmin);
  const contractsForObject = useMemo(() => {
    const all = contractsQuery.data ?? [];
    if (!regionID || !objectID) return [];
    return all.filter((c) =>
      c.meters?.some((m) => m.region_id === regionID && m.object_id === objectID),
    );
  }, [contractsQuery.data, regionID, objectID]);

  const listQuery = useRegulatoryReports({
    type: "80020",
    region_id: regionID,
    object_id: objectID,
    month: monthStr,
    ...(statusFilter && { status: statusFilter as "pending" | "generating" | "success" | "error" }),
    limit: 100,
  });
  const reports = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;

  // Completeness status — computed from loaded reports.
  const statusItems = useMemo<MonthlyStatusItem[]>(() => {
    if (!monthStr || contractsForObject.length === 0) return [];
    const monthTime = dayjs(monthStr, "YYYY-MM");
    const daysInMonth = monthTime.daysInMonth();

    return contractsForObject.map((c) => {
      const contractReports = reports.filter((r) => r.contract_id === c.id);
      const successCount = contractReports.filter((r) => r.status === "success").length;
      const errorCount = contractReports.filter((r) => r.status === "error").length;

      return {
        contract_id: c.id ?? "",
        contract_number: c.contract_number ?? "",
        company_name: c.company_name ?? "",
        region_id: regionID ?? 0,
        object_id: objectID ?? 0,
        days_in_month: daysInMonth,
        reports_success: successCount,
        reports_error: errorCount,
        reports_missing: daysInMonth - successCount - errorCount,
        meters_total:
          c.meters?.filter((m) => m.region_id === regionID && m.object_id === objectID).length ?? 0,
        meters_without_code: 0,
        is_complete: successCount === daysInMonth,
      };
    });
  }, [monthStr, contractsForObject, reports, regionID, objectID]);

  const generateMutation = useGenerateReport();

  const handleGenerate = useCallback(() => {
    if (!regionID || !objectID) return;

    const data: Record<string, unknown> = {
      type: "80020",
      region_id: regionID,
      object_id: objectID,
      round_mode: genRound,
      period_type: genPeriodType,
    };

    if (genContractId) {
      data.contract_id = genContractId;
    }

    if (genPeriodType === "day") {
      if (!genDay) return;
      data.day = genDay.format("YYYYMMDD");
    } else {
      if (!genMonth) return;
      data.month = genMonth.format("YYYY-MM");
    }

    generateMutation.mutate(
      { data: data as never },
      {
        onSuccess: () => {
          messageApi.success(
            genPeriodType === "month" ? "Генерация за месяц запущена" : "Отчёт сформирован",
          );
          setGenerateOpen(false);
          listQuery.refetch();
        },
        onError: (err) => {
          const msg = (err as { response?: { data?: { message?: string } } })?.response?.data
            ?.message;
          messageApi.error(msg || "Ошибка генерации отчёта");
        },
      },
    );
  }, [
    regionID,
    objectID,
    genDay,
    genMonth,
    genRound,
    genPeriodType,
    genContractId,
    generateMutation,
    messageApi,
    listQuery,
  ]);

  const handleDeleteConfirm = useCallback(
    (report: RegulatoryReportResponse) => {
      modal.confirm({
        title: "Пересоздать отчёт?",
        content: `Файл ${report.filename} будет перезаписан. Продолжить?`,
        okText: "Пересоздать",
        cancelText: "Отмена",
        onOk: () => {
          if (!report.region_id || !report.object_id || !report.day) return;
          generateMutation.mutate(
            {
              data: {
                type: "80020",
                region_id: report.region_id,
                object_id: report.object_id,
                day: report.day,
                round_mode: report.round_mode,
              } as never,
            },
            {
              onSuccess: () => {
                messageApi.success("Отчёт пересоздан");
                listQuery.refetch();
              },
              onError: () => {
                messageApi.error("Ошибка пересоздания отчёта");
              },
            },
          );
        },
      });
    },
    [generateMutation, messageApi, listQuery, modal],
  );

  const columns: ColumnsType<RegulatoryReportResponse> = useMemo(
    () => [
      {
        title: "Дата",
        dataIndex: "day",
        width: 120,
        render: (v?: string) => (v ? dayjs(v, "YYYYMMDD").format("DD.MM.YYYY") : "\u2014"),
        sorter: (a, b) => (a.day ?? "").localeCompare(b.day ?? ""),
        defaultSortOrder: "descend",
      },
      {
        title: "Файл",
        dataIndex: "filename",
        ellipsis: true,
        render: (v?: string, row?: RegulatoryReportResponse) => {
          const id = row?.id;
          return v && row?.status === "success" && id ? (
            <Button
              type="link"
              size="small"
              style={{ padding: 0 }}
              onClick={() => handleDownload(id, v, messageApi)}
            >
              {v}
            </Button>
          ) : (
            (v ?? "\u2014")
          );
        },
      },
      {
        title: "Статус",
        dataIndex: "status",
        width: 130,
        render: (v?: string) => {
          const info = statusMap[v ?? ""] ?? { color: "default", label: v ?? "\u2014" };
          return (
            <Tag color={info.color} icon={info.icon}>
              {info.label}
            </Tag>
          );
        },
        filters: [
          { text: "Готов", value: "success" },
          { text: "Ошибка", value: "error" },
          { text: "Ожидание", value: "pending" },
          { text: "Генерация", value: "generating" },
        ],
        onFilter: (value, record) => record.status === value,
      },
      {
        title: "Интервалы",
        width: 110,
        render: (_, row) => `${row.intervals_present ?? 0}/${row.intervals_total ?? 0}`,
      },
      {
        title: "Сумма профиля",
        dataIndex: "daily_profile_sum",
        width: 140,
        render: (v?: number | null) => (v != null ? v.toFixed(2) : "\u2014"),
      },
      {
        title: "Округление",
        dataIndex: "round_mode",
        width: 180,
        render: (v?: number) => roundModeLabels[v ?? 0] ?? "\u2014",
      },
      {
        title: "Создан",
        dataIndex: "created_at",
        width: 160,
        render: (v?: string) => (v ? dayjs(v).format("DD.MM.YYYY HH:mm") : "\u2014"),
      },
      ...(isAdmin
        ? [
            {
              title: "",
              width: 50,
              render: (_: unknown, row: RegulatoryReportResponse) =>
                row.status === "success" ? (
                  <Tooltip title="Пересоздать">
                    <Button
                      type="link"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => handleDeleteConfirm(row)}
                    />
                  </Tooltip>
                ) : null,
            },
          ]
        : []),
    ],
    [messageApi, isAdmin, handleDeleteConfirm],
  );

  return (
    <PageLayoutWithHelp
      title="Регламентные отчёты — 80020"
      extra={
        <Space>
          {regionID !== undefined && objectID !== undefined && monthStr !== undefined && (
            <Tooltip title="Скачать архив за месяц">
              <Button
                icon={<FileZipOutlined />}
                onClick={() =>
                  handleArchiveDownload("80020", regionID, objectID, monthStr, messageApi)
                }
              >
                Архив
              </Button>
            </Tooltip>
          )}
          {isAdmin && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setGenerateOpen(true)}>
              Сформировать
            </Button>
          )}
        </Space>
      }
    >
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <FilterBar>
          <FilterRow>
            <ObjectSelect
              objects={objects}
              loading={objectsQuery.isLoading}
              value={selectedObject}
              onChange={(value) => setSelectedObject(value)}
              style={{ minWidth: 320, flex: 1 }}
            />
            <DatePicker
              picker="month"
              value={month}
              onChange={setMonth}
              format="MMMM YYYY"
              allowClear={false}
              style={{ width: 200 }}
            />
            <Select
              placeholder="Статус"
              allowClear
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 160 }}
              options={[
                { label: "Готов", value: "success" },
                { label: "Ошибка", value: "error" },
                { label: "Ожидание", value: "pending" },
                { label: "Генерация", value: "generating" },
              ]}
            />
          </FilterRow>
        </FilterBar>

        {/* Completeness dashboard */}
        {statusItems.length > 0 && (
          <Collapse
            size="small"
            items={[
              {
                key: "status",
                label: "Статус формирования",
                children: (
                  <Space orientation="vertical" size="small" style={{ width: "100%" }}>
                    {statusItems.map((item) => {
                      const percent =
                        item.days_in_month > 0
                          ? Math.round((item.reports_success / item.days_in_month) * 100)
                          : 0;
                      const progressStatus = item.is_complete
                        ? ("success" as const)
                        : item.reports_error > 0
                          ? ("exception" as const)
                          : ("active" as const);

                      return (
                        <div
                          key={`${item.contract_id}-${item.region_id}-${item.object_id}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            padding: "4px 0",
                          }}
                        >
                          <Text strong style={{ minWidth: 200 }}>
                            {item.contract_number} — {item.company_name}
                          </Text>
                          <Progress
                            percent={percent}
                            status={progressStatus}
                            size="small"
                            style={{ flex: 1, minWidth: 200 }}
                            format={() => `${item.reports_success}/${item.days_in_month}`}
                          />
                          {item.reports_error > 0 && (
                            <Tooltip title={`${item.reports_error} отчётов с ошибками`}>
                              <Tag color="error" icon={<CloseCircleOutlined />}>
                                {item.reports_error}
                              </Tag>
                            </Tooltip>
                          )}
                          {item.reports_missing > 0 && (
                            <Tooltip title={`${item.reports_missing} отчётов не сформировано`}>
                              <Tag color="warning" icon={<ExclamationCircleOutlined />}>
                                {item.reports_missing}
                              </Tag>
                            </Tooltip>
                          )}
                          {item.meters_without_code > 0 && (
                            <Tooltip
                              title={`${item.meters_without_code} счётчиков без кода точки учёта — не включены в отчёты`}
                            >
                              <WarningOutlined style={{ color: "#faad14" }} />
                            </Tooltip>
                          )}
                        </div>
                      );
                    })}
                  </Space>
                ),
              },
            ]}
          />
        )}

        <EnterpriseTable<RegulatoryReportResponse>
          rowKey={(row) => row.id ?? row.filename ?? ""}
          columns={columns}
          dataSource={reports}
          loading={listQuery.isLoading}
          pagination={{ total, pageSize: 100, hideOnSinglePage: true }}
          scroll={{ x: 1100 }}
          size="small"
          toolbar={{
            reload: true,
            onReload: () => listQuery.refetch(),
          }}
        />
      </Space>

      <Modal
        title="Сформировать отчёт 80020"
        open={generateOpen}
        onCancel={() => setGenerateOpen(false)}
        onOk={handleGenerate}
        okText="Сформировать"
        cancelText="Отмена"
        confirmLoading={generateMutation.isPending}
        okButtonProps={{
          disabled:
            !regionID ||
            !objectID ||
            (genPeriodType === "day" && !genDay) ||
            (genPeriodType === "month" && !genMonth),
        }}
      >
        <Space orientation="vertical" size="middle" style={{ width: "100%", paddingTop: 8 }}>
          <div>
            <div style={{ marginBottom: 4 }}>Объект</div>
            <ObjectSelect
              objects={objects}
              loading={objectsQuery.isLoading}
              value={selectedObject}
              onChange={(value) => setSelectedObject(value)}
              style={{ width: "100%" }}
            />
          </div>
          {contractsForObject.length > 0 && (
            <div>
              <div style={{ marginBottom: 4 }}>Договор</div>
              <Select
                value={genContractId}
                onChange={setGenContractId}
                allowClear
                placeholder="Автоопределение"
                style={{ width: "100%" }}
                options={contractsForObject.map((c) => ({
                  label: `${c.contract_number} — ${c.company_name}`,
                  value: c.id,
                }))}
              />
            </div>
          )}
          <div>
            <div style={{ marginBottom: 4 }}>Период</div>
            <Radio.Group
              value={genPeriodType}
              onChange={(e) => setGenPeriodType(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              options={[
                { label: "За день", value: "day" },
                { label: "За месяц", value: "month" },
              ]}
            />
          </div>
          {genPeriodType === "day" ? (
            <div>
              <div style={{ marginBottom: 4 }}>Дата (сутки)</div>
              <DatePicker
                value={genDay}
                onChange={setGenDay}
                format="DD.MM.YYYY"
                style={{ width: "100%" }}
              />
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: 4 }}>Месяц</div>
              <DatePicker
                picker="month"
                value={genMonth}
                onChange={setGenMonth}
                format="MMMM YYYY"
                style={{ width: "100%" }}
                disabledDate={(current) => current && current >= dayjs().startOf("month")}
              />
            </div>
          )}
          <div>
            <div style={{ marginBottom: 4 }}>Округление</div>
            <Select
              value={genRound}
              onChange={setGenRound}
              style={{ width: "100%" }}
              options={[
                { label: "С накоплением остатка (рекомендуется)", value: 0 },
                { label: "Арифметическое", value: 1 },
                { label: "Без округления", value: 2 },
              ]}
            />
          </div>
        </Space>
      </Modal>
    </PageLayoutWithHelp>
  );
};

export default RegulatoryReportsPage;
